import { FC, ImgHTMLAttributes, ReactNode, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export const getInitialsFromName = (name?: string | null): string => {
  if (!name?.trim()) return '';

  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};

export type ImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src?: string | null;
  alt: string;
  className?: string;
  fallback?: ReactNode;
};

export const Image: FC<ImageProps> = ({ src, alt, className, fallback, onError, ...rest }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  const showFallback = !src || hasError;

  if (showFallback) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <span
        className={twMerge(
          'flex items-center justify-center bg-secondary-100 text-sm font-semibold text-secondary-700',
          className
        )}
        aria-label={alt}
      >
        {alt.charAt(0).toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={(event) => {
        setHasError(true);
        onError?.(event);
      }}
      {...rest}
    />
  );
};
